import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Flex,
  Image,
  Input,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";

import { InputControl, SelectControl } from "../../components/form/formControls";
import NonAvatar from "../../components/Icons/avartar";
import imageIcon from "../../assets/images/booking/image.svg";
import {
  affiliateRegisterApi,
  extractAffiliateError,
  getAffiliateInfoApi,
  uploadAffiliateImageApi,
} from "./api";

const baseURL = process.env.REACT_APP_BACKEND_API || "http://localhost:3001";

// Khối bộ phận của nhân viên, khớp EmployeeDepartmentTypeEnum ở BE
const DEPARTMENT = {
  SUPPORT_BUSINESS: 0,
  BUSINESS: 1,
};

const DEPARTMENT_LABEL = {
  [DEPARTMENT.SUPPORT_BUSINESS]: "Hỗ trợ kinh doanh",
  [DEPARTMENT.BUSINESS]: "Kinh doanh",
};

const GENDER_OPTIONS = [
  { value: 0, label: "Nam" },
  { value: 1, label: "Nữ" },
  { value: 2, label: "Khác" },
];

const toSelectOptions = (items) =>
  (items || []).map((item) => ({ value: item._id, label: item.name }));

// Ô chỉ để xem: giá trị lấy theo người giới thiệu nên không cho chỉnh
const LockedField = ({ label, value }) => (
  <Box w={"100%"}>
    <Flex alignItems="center" gap="4px" mb={"6px"}>
      <Text
        fontSize={"12px"}
        fontWeight={400}
        lineHeight={"15px"}
        color={"var(--color-label-employee)"}
      >
        {label}
      </Text>
    </Flex>
    <Flex
      alignItems={"center"}
      h={"44px"}
      px={"12px"}
      borderRadius={"8px"}
      border={"0.5px solid var(--bg-line-employee)"}
      background={"var(--fill-avatar)"}
      cursor={"not-allowed"}
    >
      <Text
        fontSize={"14px"}
        fontWeight={500}
        color={"var(--color-info-employee)"}
        noOfLines={1}
      >
        {value}
      </Text>
    </Flex>
  </Box>
);

// Ghép danh sách ô khoá thành từng cặp để xếp đúng lưới 2 cột
const toPairs = (items) => {
  const pairs = [];
  for (let i = 0; i < items.length; i += 2) pairs.push(items.slice(i, i + 2));
  return pairs;
};

const SectionTitle = ({ children }) => (
  <Text
    fontSize={"16px"}
    lineHeight={"19px"}
    fontWeight={"600"}
    color={"var(--color-header-employee)"}
    mb={"16px"}
  >
    {children}
  </Text>
);

/**
 * Một hàng 2 cột của form, mỗi ô chiếm đúng nửa hàng.
 * Bọc từng ô trong cột riêng vì InputControl và SelectControl có cách tính
 * chiều rộng khác nhau, để trực tiếp trong Flex sẽ bị lệch/vỡ.
 */
const FieldRow = ({ children }) => (
  <Flex
    alignItems={"flex-start"}
    gap={"27px"}
    mb={"12px"}
    flexWrap={{ base: "wrap", md: "nowrap" }}
  >
    {React.Children.map(children, (child) => (
      <Box flex={"1 1 0"} w={"100%"} minW={{ base: "100%", md: "0" }}>
        {child}
      </Box>
    ))}
  </Flex>
);

/**
 * Ô ảnh: chọn ảnh -> xem trước và tự upload lên hệ thống (phải upload xong mới
 * có đường dẫn ảnh để gửi kèm khi tạo tài khoản). Nút "Tải lại" chỉ hiện khi
 * upload lỗi.
 */
const ImageField = ({
  label,
  isRequired,
  variant = "card",
  value,
  preview,
  file,
  uploading,
  onPick,
  onUpload,
  onRemove,
}) => {
  const previewSrc = preview || (value ? `${baseURL}/${value}` : "");

  if (variant === "avatar") {
    return (
      <Box>
        <Flex alignItems="center" gap="4px" mb={"16px"}>
          <Text
            fontSize={"16px"}
            lineHeight={"19px"}
            fontWeight={"600"}
            color={"var(--color-header-employee)"}
          >
            {label}
          </Text>
          {isRequired && (
            <Text fontSize={"16px"} lineHeight={"19px"} color={"red"}>
              *
            </Text>
          )}
        </Flex>
        <Flex alignItems={"center"} gap={"16px"} flexWrap={"wrap"}>
          <Box position={"relative"}>
            <Flex
              w={"120px"}
              h={"120px"}
              borderRadius={"50%"}
              bgColor={"var(--fill-avatar)"}
              alignItems={"center"}
              justifyContent={"center"}
              overflow={"hidden"}
            >
              {previewSrc ? (
                <Avatar src={previewSrc} size="2xl" name="" />
              ) : (
                <NonAvatar />
              )}
            </Flex>
            <Input
              type="file"
              accept="image/png, image/jpeg"
              opacity={0}
              position="absolute"
              top={0}
              left={0}
              width="100%"
              height="100%"
              cursor="pointer"
              onChange={onPick}
            />
          </Box>
          <Box>
            {file && (
              <>
                <Button
                  bg={"var(--linear-gradient-employee)"}
                  color={"var(--color-main)"}
                  fontSize={"12px"}
                  fontWeight={"600"}
                  _hover={{ filter: "brightness(90%)" }}
                  h={"28px"}
                  minH={"28px"}
                  w={"90px"}
                  isLoading={uploading}
                  onClick={onUpload}
                >
                  Tải lại
                </Button>
                <Text fontSize={"12px"} color={"red"} mt={"6px"} maxW={"220px"}>
                  {uploading
                    ? "Đang tải ảnh lên..."
                    : "Tải ảnh lên thất bại, vui lòng bấm Tải lại"}
                </Text>
              </>
            )}
            {!file && value && (
              <Text fontSize={"12px"} color={"var(--color-label-employee)"}>
                Đã tải ảnh lên hệ thống
              </Text>
            )}
          </Box>
        </Flex>
      </Box>
    );
  }

  return (
    <Box flex={"1 1 0"} w={"100%"} minW={{ base: "100%", md: "0" }}>
      <Flex alignItems="center" gap="4px">
        <Text color={"var(--color-label-employee)"} fontSize={"12px"}>
          {label}
        </Text>
        {isRequired && (
          <Text fontSize={"12px"} color={"red"} lineHeight={"15px"}>
            *
          </Text>
        )}
      </Flex>

      <Flex
        mt={"8px"}
        border={"0.5px solid var(--bg-line-employee)"}
        borderRadius={"8px"}
        justifyContent={"center"}
        position={"relative"}
      >
        <Box
          overflow={"hidden"}
          w={"100%"}
          h={"220px"}
          borderRadius={"11px"}
          position={"relative"}
        >
          {previewSrc && (
            <Image
              src={previewSrc}
              objectFit={"contain"}
              h={"100%"}
              w={"100%"}
            />
          )}
        </Box>

        {previewSrc && (
          <Box
            as="button"
            type="button"
            position="absolute"
            top="8px"
            right="8px"
            zIndex={2}
            bg={"var(--color-main)"}
            borderRadius={"50%"}
            w={"28px"}
            h={"28px"}
            boxShadow={"0 0 4px rgba(0,0,0,0.15)"}
            onClick={onRemove}
          >
            <Text fontSize={"14px"} color={"var(--color-label-employee)"}>
              ×
            </Text>
          </Box>
        )}

        <Flex
          position={"absolute"}
          right={"8px"}
          bottom={"8px"}
          cursor={"pointer"}
        >
          <Box
            padding={"8px"}
            bg={"var(--fill-avatar)"}
            borderRadius={"50%"}
            position={"relative"}
          >
            <Input
              type="file"
              accept="image/*"
              opacity={0}
              position="absolute"
              top={0}
              left={0}
              width="100%"
              height="100%"
              cursor="pointer"
              onChange={onPick}
            />
            <Image src={imageIcon} />
          </Box>
        </Flex>
      </Flex>

      {file && (
        <Flex
          w={"full"}
          justifyContent={"flex-end"}
          alignItems={"center"}
          gap={"12px"}
          mt={2}
        >
          <Text fontSize={"12px"} color={"red"}>
            {uploading
              ? "Đang tải ảnh lên..."
              : "Tải ảnh lên thất bại, vui lòng bấm Tải lại"}
          </Text>
          <Button
            bg={"var(--linear-gradient-employee)"}
            color={"var(--color-main)"}
            fontSize={"12px"}
            fontWeight={"600"}
            _hover={{ filter: "brightness(90%)" }}
            h={"28px"}
            minH={"28px"}
            w={"90px"}
            isLoading={uploading}
            onClick={onUpload}
          >
            Tải lại
          </Button>
        </Flex>
      )}
    </Box>
  );
};

const PageShell = ({ children }) => (
  <Box minH={"100vh"} bg={"var(--color-backgroundmain)"} py={"32px"} px={"16px"}>
    <Box
      maxW={"860px"}
      m={"0 auto"}
      bg={"var(--color-main)"}
      borderRadius={"16px"}
      p={{ base: "20px", md: "32px" }}
    >
      {children}
    </Box>
  </Box>
);

const AffiliateRegister = () => {
  const { code } = useParams();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [info, setInfo] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [createdEmployee, setCreatedEmployee] = useState(null);

  // Ảnh: giữ file đang chọn + đường dẫn đã upload lên hệ thống
  const [images, setImages] = useState({
    avatar: {},
    idCardFrontImage: {},
    idCardBackImage: {},
    vneidFrontImage: {},
    vneidBackImage: {},
  });

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    clearErrors,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(
      Yup.object().shape({
        fullName: Yup.string().trim().required("Vui lòng nhập họ tên"),
        gender: Yup.number()
          .transform((value, originalValue) =>
            originalValue === "" || originalValue === null ? undefined : value
          )
          .typeError("Vui lòng chọn giới tính")
          .required("Vui lòng chọn giới tính"),
        phone: Yup.string()
          .trim()
          .required("Vui lòng nhập số điện thoại")
          .matches(/^[0-9]{10,12}$/, "Số điện thoại không hợp lệ."),
        email: Yup.string()
          .trim()
          .required("Vui lòng nhập email")
          .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, "Email không hợp lệ"),
        identificationNumber: Yup.string()
          .trim()
          .required("Vui lòng nhập số CCCD/CMND")
          .matches(/^\d{12}$/, "Số CCCD/CMND phải là 12 chữ số"),
        branchId: Yup.string().required("Vui lòng chọn chi nhánh"),
        jobTitleId: Yup.string().required("Vui lòng chọn chức danh"),
        bankAccount: Yup.string().trim().required("Vui lòng nhập số tài khoản Techcombank"),
        contractEmail: Yup.string()
          .trim()
          .required("Vui lòng nhập email nhận hợp đồng dịch vụ")
          .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, "Email không hợp lệ"),
      })
    ),
  });

  const referrer = info?.referrer;

  // Khối bộ phận, vùng, khối, phòng ban, loại nhân sự đều lấy cứng theo người
  // giới thiệu nên không đưa vào form, chỉ hiển thị để người đăng ký biết.
  const department = Number(referrer?.department);
  const isBusiness = department === DEPARTMENT.BUSINESS;

  const options = useMemo(() => info?.options || {}, [info]);

  // Chức vụ luôn là "Nhân viên" của khối bộ phận người giới thiệu đang thuộc
  const staffJobPosition = useMemo(() => {
    const list = options.jobPositionsByDepartment?.[department] || [];
    return list.find((item) => item.code === info?.defaultJobPositionCode);
  }, [options.jobPositionsByDepartment, department, info]);

  // Các ô lấy theo người giới thiệu; người giới thiệu không có thì không hiện
  const lockedOrgFields = useMemo(() => {
    if (!referrer) return [];

    return [
      { label: "Khối bộ phận", value: DEPARTMENT_LABEL[department] },
      { label: "Vùng", value: referrer.saleArea?.name },
      { label: "Khối", value: referrer.saleBlock?.name },
      { label: "Phòng ban", value: referrer.supportDepartment?.name },
      { label: "Loại nhân sự", value: referrer.businessCategory?.name },
    ].filter((item) => !!item.value);
  }, [referrer, department]);

  useEffect(() => {
    let active = true;

    (async () => {
      setLoading(true);
      try {
        const data = await getAffiliateInfoApi(code);
        if (!active) return;
        setInfo(data);
      } catch (error) {
        if (!active) return;
        setLoadError(
          extractAffiliateError(error, "Link giới thiệu không hợp lệ")
        );
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [code]);

  // Chi nhánh mặc định theo người giới thiệu nhưng vẫn cho chọn lại
  useEffect(() => {
    if (referrer?.branch?._id) setValue("branchId", referrer.branch._id);
  }, [referrer, setValue]);

  const setField = useCallback(
    (name, value) => {
      setValue(name, value === null ? undefined : value);
      clearErrors(name);
    },
    [setValue, clearErrors]
  );

  // Upload 1 ảnh; chỉ cập nhật khi ô đó vẫn đang giữ đúng file này (người dùng
  // có thể đã xoá hoặc chọn ảnh khác trong lúc đang upload)
  const uploadImageFile = useCallback(
    async (field, file) => {
      if (!file) return;

      const updateField = (getData) =>
        setImages((prev) =>
          prev[field]?.file === file
            ? { ...prev, [field]: getData(prev[field]) }
            : prev
        );

      updateField((current) => ({ ...current, uploading: true }));

      try {
        const { filePath } = await uploadAffiliateImageApi(code, file);

        updateField((current) => ({ path: filePath, preview: current?.preview }));
        toast.success("Upload ảnh thành công");
      } catch (error) {
        updateField((current) => ({ ...current, uploading: false }));
        toast.error(extractAffiliateError(error, "Upload ảnh thất bại"));
      }
    },
    [code]
  );

  const pickImage = useCallback(
    (field) => (event) => {
      const file = event.target.files?.[0];
      event.target.value = "";
      if (!file) return;

      setImages((prev) => ({
        ...prev,
        [field]: { file, preview: URL.createObjectURL(file) },
      }));
      // Chọn ảnh là tự upload, không cần bấm nút
      uploadImageFile(field, file);
    },
    [uploadImageFile]
  );

  const removeImage = useCallback(
    (field) => () => {
      setImages((prev) => ({ ...prev, [field]: {} }));
    },
    []
  );

  // Nút "Tải lại" khi upload lỗi
  const uploadImage = useCallback(
    (field) => () => uploadImageFile(field, images[field]?.file),
    [images, uploadImageFile]
  );

  const onSubmit = async (values) => {
    if (!images.avatar?.path) {
      toast.error("Vui lòng tải ảnh đại diện lên hệ thống");
      return;
    }
    if (!images.idCardFrontImage?.path) {
      toast.error("Vui lòng tải ảnh CCCD mặt trước lên hệ thống");
      return;
    }
    if (!images.idCardBackImage?.path) {
      toast.error("Vui lòng tải ảnh CCCD mặt sau lên hệ thống");
      return;
    }

    setSubmitting(true);
    try {
      // Khối bộ phận / vùng / khối / phòng ban / loại nhân sự gửi thẳng theo
      // người giới thiệu; BE cũng tự fallback về người giới thiệu nếu thiếu.
      const body = {
        fullName: values.fullName?.trim(),
        gender: Number(values.gender),
        phone: values.phone?.trim(),
        email: values.email?.trim(),
        identificationNumber: values.identificationNumber?.trim(),
        branchId: values.branchId,
        jobTitleId: values.jobTitleId,
        bankAccount: values.bankAccount?.trim(),
        contractEmail: values.contractEmail?.trim(),
        avatar: images.avatar.path,
        idCardFrontImage: images.idCardFrontImage.path,
        idCardBackImage: images.idCardBackImage.path,
        // Ảnh VNeID không bắt buộc (chưa tải thì không gửi)
        vneidFrontImage: images.vneidFrontImage?.path || undefined,
        vneidBackImage: images.vneidBackImage?.path || undefined,
        ...(staffJobPosition?._id
          ? { jobPositionId: staffJobPosition._id }
          : {}),
        ...(department === DEPARTMENT.BUSINESS ||
        department === DEPARTMENT.SUPPORT_BUSINESS
          ? { department }
          : {}),
        ...(isBusiness
          ? {
              ...(referrer?.saleArea?._id
                ? { saleAreaId: referrer.saleArea._id }
                : {}),
              ...(referrer?.saleBlock?._id
                ? { saleBlockId: referrer.saleBlock._id }
                : {}),
              ...(referrer?.businessCategory?._id
                ? { businessCategoryId: referrer.businessCategory._id }
                : {}),
            }
          : {
              ...(referrer?.supportDepartment?._id
                ? { supportDepartmentId: referrer.supportDepartment._id }
                : {}),
            }),
      };

      const data = await affiliateRegisterApi(code, body);
      setCreatedEmployee(data);
    } catch (error) {
      toast.error(extractAffiliateError(error, "Đăng ký thất bại"));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Flex
        minH={"100vh"}
        align={"center"}
        justify={"center"}
        bg={"var(--color-backgroundmain)"}
      >
        <Spinner
          thickness="4px"
          speed="0.65s"
          color="var(--color-secondary)"
          size="xl"
        />
      </Flex>
    );
  }

  if (loadError) {
    return (
      <PageShell>
        <Text
          fontSize={"18px"}
          fontWeight={"600"}
          color={"var(--color-header-employee)"}
        >
          Không mở được link giới thiệu
        </Text>
        <Text fontSize={"14px"} color={"var(--color-label-employee)"} mt={"12px"}>
          {loadError}
        </Text>
        <Text fontSize={"14px"} color={"var(--color-label-employee)"} mt={"8px"}>
          Vui lòng liên hệ người đã gửi link cho bạn để nhận link mới.
        </Text>
      </PageShell>
    );
  }

  if (createdEmployee) {
    return (
      <PageShell>
        <Text
          fontSize={"18px"}
          fontWeight={"600"}
          color={"var(--color-header-employee)"}
        >
          Đăng ký thành công
        </Text>
        <Box
          mt={"16px"}
          p={"16px"}
          borderRadius={"8px"}
          background={"var(--fill-avatar)"}
        >
          <Text fontSize={"14px"} color={"var(--color-info-employee)"}>
            Mã nhân viên của bạn: <b>{createdEmployee?.code}</b>
          </Text>
          <Text fontSize={"14px"} color={"var(--color-info-employee)"} mt={"8px"}>
            Thông tin đăng nhập đã được gửi tới email{" "}
            <b>{createdEmployee?.email}</b>.
          </Text>
        </Box>
        <Text fontSize={"14px"} color={"var(--color-label-employee)"} mt={"16px"}>
          Hồ sơ của bạn đang chờ bộ phận nhân sự xác nhận hoàn thiện. Tài khoản
          sẽ tự dừng hoạt động nếu sau 7 ngày hồ sơ chưa được xác nhận.
        </Text>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <Text
        fontSize={"18px"}
        fontWeight={"600"}
        color={"var(--color-header-employee)"}
      >
        Đăng ký nhân viên mới
      </Text>
      <Text fontSize={"14px"} color={"var(--color-label-employee)"} mt={"8px"}>
        Bạn được giới thiệu bởi <b>{referrer?.fullName}</b> ({referrer?.code})
      </Text>

      <Box
        w={"100%"}
        h={"1px"}
        backgroundColor={"var(--bg-line-employee)"}
        my={"24px"}
      />

      <form onSubmit={handleSubmit(onSubmit)}>
        <ImageField
          label="Ảnh đại diện"
          isRequired
          variant="avatar"
          value={images.avatar?.path}
          preview={images.avatar?.preview}
          file={images.avatar?.file}
          uploading={images.avatar?.uploading}
          onPick={pickImage("avatar")}
          onUpload={uploadImage("avatar")}
        />

        <Box mt={"24px"}>
          <SectionTitle>Thông tin cá nhân</SectionTitle>

          <FieldRow>
            <InputControl
              label="Họ và tên"
              placeholder="Nhập họ và tên"
              errors={errors.fullName}
              register={register("fullName")}
              mwidth={"100%"}
              isRequired={true}
            />
            <SelectControl
              label="Chi nhánh"
              dataOption={toSelectOptions(options.branches)}
              selectedMain={watch("branchId")}
              placeholder="Chọn chi nhánh"
              onChange={(value) => setField("branchId", value)}
              errorMessage={errors.branchId}
              registerProps={register("branchId")}
              isSearchable={true}
              mwidth={"100%"}
              isRequired={true}
            />
          </FieldRow>

          <FieldRow>
            <SelectControl
              label="Giới tính"
              dataOption={GENDER_OPTIONS}
              selectedMain={watch("gender")}
              placeholder="Chọn giới tính"
              onChange={(value) => setField("gender", value)}
              errorMessage={errors.gender}
              registerProps={register("gender")}
              mwidth={"100%"}
              isRequired={true}
            />
            <InputControl
              type="number"
              label="Số điện thoại"
              placeholder="Nhập số điện thoại"
              errors={errors.phone}
              register={register("phone")}
              mwidth={"100%"}
              isRequired={true}
            />
          </FieldRow>

          <FieldRow>
            <InputControl
              type="email"
              label="Email"
              placeholder="Nhập email"
              errors={errors.email}
              register={register("email")}
              mwidth={"100%"}
              isRequired={true}
            />
            <InputControl
              type="number"
              label="Số CCCD/CMND"
              placeholder="Nhập số CCCD/CMND"
              errors={errors.identificationNumber}
              register={register("identificationNumber")}
              mwidth={"100%"}
              isRequired={true}
            />
          </FieldRow>

          <FieldRow>
            <LockedField
              label="Chức vụ"
              value={staffJobPosition?.name || "Nhân viên"}
            />
            <SelectControl
              label="Chức danh"
              dataOption={toSelectOptions(options.jobTitles)}
              selectedMain={watch("jobTitleId")}
              placeholder="Chọn chức danh"
              onChange={(value) => setField("jobTitleId", value)}
              errorMessage={errors.jobTitleId}
              registerProps={register("jobTitleId")}
              isSearchable={true}
              mwidth={"100%"}
              isRequired={true}
            />
          </FieldRow>

          {/* Các thông tin lấy theo người giới thiệu, chỉ hiển thị chứ không cho sửa */}
          {toPairs(lockedOrgFields).map((pair) => (
            <FieldRow key={pair[0].label}>
              <LockedField label={pair[0].label} value={pair[0].value} />
              {pair[1] ? (
                <LockedField label={pair[1].label} value={pair[1].value} />
              ) : (
                <Box />
              )}
            </FieldRow>
          ))}
        </Box>

        <Box mt={"24px"}>
          <SectionTitle>Thông tin thanh toán</SectionTitle>
          <FieldRow>
            <InputControl
              label="Số tài khoản Techcombank"
              placeholder="Nhập số tài khoản"
              errors={errors.bankAccount}
              register={register("bankAccount")}
              mwidth={"100%"}
              isRequired={true}
            />
            <InputControl
              type="email"
              label="Email nhận hợp đồng dịch vụ"
              placeholder="Nhập email nhận hợp đồng dịch vụ"
              errors={errors.contractEmail}
              register={register("contractEmail")}
              mwidth={"100%"}
              isRequired={true}
            />
          </FieldRow>
        </Box>

        <Box mt={"24px"}>
          <SectionTitle>Thông tin giấy tờ</SectionTitle>
          <Flex gap={"27px"} flexWrap={{ base: "wrap", md: "nowrap" }}>
            <ImageField
              label="Ảnh CCCD mặt trước"
              isRequired
              value={images.idCardFrontImage?.path}
              preview={images.idCardFrontImage?.preview}
              file={images.idCardFrontImage?.file}
              uploading={images.idCardFrontImage?.uploading}
              onPick={pickImage("idCardFrontImage")}
              onUpload={uploadImage("idCardFrontImage")}
              onRemove={removeImage("idCardFrontImage")}
            />
            <ImageField
              label="Ảnh CCCD mặt sau"
              isRequired
              value={images.idCardBackImage?.path}
              preview={images.idCardBackImage?.preview}
              file={images.idCardBackImage?.file}
              uploading={images.idCardBackImage?.uploading}
              onPick={pickImage("idCardBackImage")}
              onUpload={uploadImage("idCardBackImage")}
              onRemove={removeImage("idCardBackImage")}
            />
          </Flex>
          <Flex
            gap={"27px"}
            mt={"24px"}
            flexWrap={{ base: "wrap", md: "nowrap" }}
          >
            <ImageField
              label="Ảnh VNeID mặt trước"
              value={images.vneidFrontImage?.path}
              preview={images.vneidFrontImage?.preview}
              file={images.vneidFrontImage?.file}
              uploading={images.vneidFrontImage?.uploading}
              onPick={pickImage("vneidFrontImage")}
              onUpload={uploadImage("vneidFrontImage")}
              onRemove={removeImage("vneidFrontImage")}
            />
            <ImageField
              label="Ảnh VNeID mặt sau"
              value={images.vneidBackImage?.path}
              preview={images.vneidBackImage?.preview}
              file={images.vneidBackImage?.file}
              uploading={images.vneidBackImage?.uploading}
              onPick={pickImage("vneidBackImage")}
              onUpload={uploadImage("vneidBackImage")}
              onRemove={removeImage("vneidBackImage")}
            />
          </Flex>
        </Box>

        <Flex justifyContent={"flex-end"} mt={"32px"}>
          <Button
            type="submit"
            bg={"var(--linear-gradient-employee)"}
            color={"var(--color-main)"}
            fontSize={"14px"}
            fontWeight={"600"}
            _hover={{ filter: "brightness(90%)" }}
            h={"44px"}
            minW={"180px"}
            isLoading={submitting}
          >
            Tạo tài khoản
          </Button>
        </Flex>
      </form>
    </PageShell>
  );
};

export default AffiliateRegister;
